import {
	AnchorHTMLAttributes,
	ButtonHTMLAttributes,
	forwardRef,
	ReactNode,
} from 'react';
import {
	Anchor,
	Box,
	BoxProps,
	Tooltip,
	TooltipProps,
	UnstyledButton,
	UnstyledButtonProps,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import ArrowLeftIcon from '@/assets/icons/arrowLongLeft.svg?react';
import ArrowRightIcon from '@/assets/icons/arrowLongRight.svg?react';
import classNames from 'classnames';
import styles from './Button.module.scss';

interface BaseProps {
	themes: (
		| 'primary'
		| 'secondary'
		| 'success'
		| 'warning'
		| 'danger'
		| 'arrow'
		| 'arrowReverse'
		| 'icon'
		| 'iconOnly'
		| 'hiddeTextMobile'
		| 'fadeIn'
		| 'fullWidth'
		| 'transparent'
		| 'fit'
	)[];
	children?: ReactNode;
	image?: string;
	size?: 'sm' | 'lg';
	tooltip?: string;
	tooltipProps?: Partial<TooltipProps>;
	disabled?: boolean;
	wrapperProps?: BoxProps;
}

interface ButtonProps {
	isLink?: never;
	to?: never;
	buttonProps?: ButtonHTMLAttributes<HTMLButtonElement> & UnstyledButtonProps;
}

interface LinkProps {
	isLink: true;
	to: string;
	buttonProps?: AnchorHTMLAttributes<HTMLAnchorElement> & UnstyledButtonProps;
}

type Props = BaseProps & (ButtonProps | LinkProps);

export const Button = forwardRef<HTMLButtonElement, Props>((props, ref) => {
	const isLink = checkIfLink(props);

	const className = classNames(
		styles.button,
		...props.themes.map((style) => styles[style]),
		props.buttonProps?.className,
		{
			[styles.small]: props.size === 'sm',
			[styles.disabled]: !!props.disabled,
		}
	);

	const children = (
		<>
			{props.themes.includes('arrowReverse') && <ArrowLeftIcon />}
			{props.children}
			{props.themes.includes('arrow') && <ArrowRightIcon />}
		</>
	);

	return (
		<Tooltip
			label={props.tooltip}
			disabled={!props.tooltip}
			{...props.tooltipProps}
		>
			<Box
				{...props.wrapperProps}
				w={props.themes.includes('fullWidth') ? '100%' : undefined}
			>
				{isLink ? (
					<Anchor
						{...props.buttonProps}
						component={Link}
						className={className}
						to={props.to}
					>
						{children}
					</Anchor>
				) : (
					<UnstyledButton
						ref={ref}
						{...props.buttonProps}
						className={className}
					>
						{children}
					</UnstyledButton>
				)}
			</Box>
		</Tooltip>
	);
});

function checkIfLink(props: ButtonProps | LinkProps): props is LinkProps {
	return !!props.isLink;
}
